/**
 * Format agenda events from API response
 * Shared utility for User Dashboard and Organization Dashboard
 *
 * @param {Object} agendaApiResponse - API response {data: {agenda: {timestamp: {events: [...]}}}}
 * @param {Object} options - Formatting options
 * @param {string} options.userTimeZone - User's timezone (optional, defaults to browser timezone)
 * @param {Object} options.timezonesMap - Map of location_id -> timezone (optional, for User Dashboard)
 * @returns {Array} Array of formatted events
 */
export const formatAgendaEvents = (agendaApiResponse, options = {}) => {
  const agendaObj = agendaApiResponse?.data?.agenda || {};
  const agendaArray = [];

  // Get user timezone from options or use browser timezone
  const userTimeZone = options.userTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Optional timezonesMap for User Dashboard (location_id -> timezone)
  const timezonesMap = options.timezonesMap || {};

  /**
   * Validate if timezone string is valid IANA timezone
   */
  const isValidTimeZone = (tz) => {
    if (!tz || typeof tz !== 'string') return false;
    try {
      Intl.DateTimeFormat(undefined, { timeZone: tz });
      return true;
    } catch (e) {
      return false;
    }
  };

  /**
   * Extract timezone string from various formats (string or object with id/name)
   */
  const extractTimezone = (tzValue) => {
    if (!tzValue) return null;
    if (typeof tzValue === 'string' && isValidTimeZone(tzValue)) {
      return tzValue;
    }
    if (typeof tzValue === 'object') {
      if (tzValue.id && isValidTimeZone(tzValue.id)) return tzValue.id;
      if (tzValue.name && isValidTimeZone(tzValue.name)) return tzValue.name;
    }
    return null;
  };

  /**
   * Get location timezone with priority fallback:
   * 1. timezonesMap by location_id (User Dashboard)
   * 2. event.locationTimezoneId (Organization Dashboard)
   * 3. event.locationTimeZone
   * 4. event.timezone
   * 5. userTimeZone (fallback)
   */
  const getLocationTimeZone = (event) => {
    // Priority 1: timezonesMap by location_id
    const locationId = event.location_id || event.locationId;
    if (locationId && timezonesMap[locationId]) {
      const tz = extractTimezone(timezonesMap[locationId]);
      if (tz) return tz;
    }

    // Priority 2: event.locationTimezoneId (direct string from Organization API)
    if (event.locationTimezoneId && isValidTimeZone(event.locationTimezoneId)) {
      return event.locationTimezoneId;
    }

    // Priority 3: event.locationTimeZone (object or string)
    const tzFromLocationTimeZone = extractTimezone(event.locationTimeZone);
    if (tzFromLocationTimeZone) return tzFromLocationTimeZone;

    // Priority 4: event.timezone
    const tzFromTimezone = extractTimezone(event.timezone);
    if (tzFromTimezone) return tzFromTimezone;

    // Fallback to user timezone
    return userTimeZone;
  };

  /**
   * Format time range with timezone abbreviation
   */
  const formatTimeRange = (startTimestamp, finishTimestamp, timeZone) => {
    try {
      const startDate = new Date(startTimestamp * 1000);
      const finishDate = new Date(finishTimestamp * 1000);

      // Validate dates
      if (isNaN(startDate.getTime()) || isNaN(finishDate.getTime())) {
        throw new Error('Invalid timestamp');
      }

      const startTime = startDate.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: timeZone
      });

      const endTime = finishDate.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: timeZone
      });

      // Get timezone abbreviation
      const tzFormatter = startDate.toLocaleTimeString('en-US', {
        timeZoneName: 'short',
        timeZone: timeZone
      });
      const tzShort = tzFormatter.split(' ').pop();

      return {
        formatted: `${startTime} to ${endTime} [${tzShort}]`,
        startTime,
        endTime,
        tzShort
      };
    } catch (e) {
      console.error('Error formatting time:', e, 'timeZone:', timeZone);
      return {
        formatted: '00:00 to 00:00 [UTC]',
        startTime: '00:00',
        endTime: '00:00',
        tzShort: 'UTC'
      };
    }
  };

  /**
   * Format date string
   */
  const formatDate = (timestamp, timeZone) => {
    const eventDate = new Date(timestamp * 1000);
    try {
      return eventDate.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: timeZone
      });
    } catch (e) {
      // Fallback without timezone if invalid
      return eventDate.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  };

  /**
   * Calculate day timestamp (midnight in location timezone)
   */
  const calculateDayTimestamp = (startTimestamp, timeZone) => {
    try {
      const startDate = new Date(startTimestamp * 1000);

      // Get time components in location timezone
      const timeFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timeZone,
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: false
      });

      const timeStr = timeFormatter.format(startDate);
      const [hours, minutes, seconds] = timeStr.split(':').map(Number);

      // Calculate seconds since midnight in location timezone
      const secondsSinceMidnight = hours * 3600 + minutes * 60 + (seconds || 0);

      // Subtract from event timestamp to get midnight timestamp
      return startTimestamp - secondsSinceMidnight;
    } catch (e) {
      // Fallback: use dateKey or approximate
      return startTimestamp - (startTimestamp % 86400);
    }
  };

  // Iterate through timestamp keys and collect all events
  Object.keys(agendaObj).forEach(dateKey => {
    const dateData = agendaObj[dateKey];
    const events = dateData.events || [];

    // Format each event
    events.forEach(event => {
      // Get location timezone with fallbacks
      const locationTimeZone = getLocationTimeZone(event);

      // Format date in location timezone
      const formattedDate = formatDate(event.startTime, locationTimeZone);

      // Format user time
      const userTime = formatTimeRange(event.startTime, event.finishTime, userTimeZone);

      // Format location time
      const locationTime = formatTimeRange(event.startTime, event.finishTime, locationTimeZone);

      // Calculate day timestamp (midnight in location timezone)
      const dayTimestamp = calculateDayTimestamp(event.startTime, locationTimeZone);

      const formattedEvent = {
        id: event.id || Math.random().toString(36).substring(2, 11),
        date: formattedDate,
        type: event.type || 'Flight',
        organization: event.organizationName || 'Unknown Organization',
        organizationId: event.organizationId || event.organization_id || null,
        organizationUnique: event.organizationUnique || event.organization || '',
        location: event.locationName || 'Unknown Location',
        aircraft: event.registrationNumber || event.regNumb || event.resourceName || event.aircraftName || event.aircraft || (event.resources?.find?.(r => r.typeName === 'Aircraft')?.name) || (event.simulator ? 'Simulator' : 'Unknown Aircraft'),
        pilot: `${event.pilotName || ''} ${event.pilotSurname || ''}`.trim() || 'Unknown Pilot',
        instructor: `${event.instructorName || ''} ${event.instructorSurname || ''}`.trim() || null,
        activityTypeName: event.activityTypeName || null,
        activityTypeColor: event.activityTypeColor || null,
        simulator: event.simulator || false,
        restricted: event.restricted || false,
        yourTime: userTime.formatted,
        locationTime: locationTime.formatted,
        locationTimeZone: locationTimeZone,
        // Store original timestamps for calendar link
        startTime: event.startTime,
        finishTime: event.finishTime,
        dayTimestamp: dayTimestamp
      };

      agendaArray.push(formattedEvent);
    });
  });

  // Deduplicate events by ID (events spanning multiple days may appear in multiple date keys)
  const seenIds = new Set();
  const uniqueAgendaArray = agendaArray.filter(event => {
    if (seenIds.has(event.id)) {
      return false;
    }
    seenIds.add(event.id);
    return true;
  });

// Sort events by start time
  uniqueAgendaArray.sort((a, b) => a.startTime - b.startTime);

  return uniqueAgendaArray;
};
